import { createSlice } from "@reduxjs/toolkit";
import { message } from "antd";
import { PAYMENT_MODE_CASH } from "../utils/constants";

/* -------------------- HELPERS -------------------- */

const generateTabId = () =>
  `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const normalizeTaxRate = (rate) => {
  if (rate === null || rate === undefined) return 0;
  if (String(rate).toLowerCase() === "exempt") return 0;
  const numericRate = parseFloat(String(rate).replace("%", ""));
  return isNaN(numericRate) ? 0 : numericRate;
};

const calculateTax = (price, taxRate) =>
  Number(((price * taxRate) / 100).toFixed(2));

/** Tax rate for an item: use stored taxRate (from backend salesTax at add) or fallback to salesTax. */
const getItemTaxRate = (item) => {
  if (item.taxRate != null) return item.taxRate;
  return normalizeTaxRate(item.salesTax);
};

/** Returns initial state: { activeTabId, tabs }. Migrates old single-cart localStorage if needed. */
const getInitialState = () => {
  try {
    const saved = JSON.parse(localStorage.getItem("cart") || "{}");

    // New format: multi-tab
    if (saved.activeTabId && saved.tabs && typeof saved.tabs === "object") {
      const tabIds = Object.keys(saved.tabs);
      if (tabIds.length > 0 && tabIds.includes(saved.activeTabId)) {
        // normalize potentially older persisted tabs
        tabIds.forEach((id) => ensureTabState(saved.tabs[id]));
        return {
          activeTabId: saved.activeTabId,
          tabs: saved.tabs,
        };
      }
    }

    // Old format: single cart → migrate to one tab
    const cartItems = Array.isArray(saved.cartItems) ? saved.cartItems : [];
    const total = Number(saved.total) || 0;
    const tabId = generateTabId();
    return {
      activeTabId: tabId,
      tabs: {
        [tabId]: {
          cartItems,
          total,
          paymentMode: PAYMENT_MODE_CASH,
          paymentModeTouched: false,
        },
      },
    };
  } catch {
    const tabId = generateTabId();
    return {
      activeTabId: tabId,
      tabs: {
        [tabId]: {
          cartItems: [],
          total: 0,
          paymentMode: PAYMENT_MODE_CASH,
          paymentModeTouched: false,
        },
      },
    };
  }
};

function ensureTabState(tab) {
  if (!tab) return;
  if (!Array.isArray(tab.cartItems)) tab.cartItems = [];
  if (typeof tab.total !== "number") tab.total = 0;
  if (typeof tab.paymentMode !== "string" || !tab.paymentMode.trim()) {
    tab.paymentMode = PAYMENT_MODE_CASH;
  }
  if (typeof tab.paymentModeTouched !== "boolean") {
    tab.paymentModeTouched = false;
  }
}

const getActiveTab = (state) => {
  if (!state.activeTabId || !state.tabs) return null;
  return state.tabs[state.activeTabId] ?? null;
};

const ensureActiveTabExists = (state) => {
  if (!state.tabs) state.tabs = {};
  let tab = state.tabs[state.activeTabId];
  if (!tab) {
    const id = state.activeTabId || generateTabId();
    state.activeTabId = id;
    state.tabs[id] = {
      cartItems: [],
      total: 0,
      paymentMode: PAYMENT_MODE_CASH,
      paymentModeTouched: false,
    };
    tab = state.tabs[id];
  }
  ensureTabState(tab);
  return tab;
};

/* -------------------- SLICE -------------------- */

const cartSlice = createSlice({
  name: "cart",
  initialState: getInitialState(),

  reducers: {
    /* ---------- TAB ACTIONS ---------- */
    addCartTab: (state) => {
      if (!state.tabs) state.tabs = {};
      const tabId = generateTabId();
      state.tabs[tabId] = {
        cartItems: [],
        total: 0,
        paymentMode: PAYMENT_MODE_CASH,
        paymentModeTouched: false,
      };
      state.activeTabId = tabId;
    },

    removeCartTab: (state, action) => {
      const tabId = action.payload;
      if (!state.tabs || !state.tabs[tabId]) return;
      const tabIds = Object.keys(state.tabs);
      if (tabIds.length <= 1) return; // keep at least one tab
      delete state.tabs[tabId];
      if (state.activeTabId === tabId) {
        state.activeTabId = tabIds.find((id) => id !== tabId) || tabIds[0];
      }
    },

    setActiveCartTab: (state, action) => {
      const tabId = action.payload;
      if (state.tabs && state.tabs[tabId]) {
        state.activeTabId = tabId;
      }
    },

    /* ---------- ADD PRODUCT ---------- */
    addProduct: (state, action) => {
      const tab = ensureActiveTabExists(state);
      if (!tab) return;

      const taxRate = normalizeTaxRate(action.payload.salesTax);

      const rawPrice = Number(action.payload.price) || 0;
      const rawUnit = Number(action.payload.unit) || 0;
      const unitPrice =
        rawUnit && rawUnit !== 0
          ? Number((rawPrice / rawUnit).toFixed(2))
          : rawPrice;

      const itemTax = calculateTax(unitPrice, taxRate);

      const existingItem = tab.cartItems.find(
        (i) => i._id === action.payload._id
      );

      if (existingItem) {
        existingItem.quantity += 1;
        existingItem.tax += itemTax;
        existingItem.totalPrice += unitPrice + itemTax;
      } else {
        tab.cartItems.push({
          ...action.payload,
          price: unitPrice,
          quantity: 1,
          taxRate,
          tax: itemTax,
          totalPrice: unitPrice + itemTax,
          discount: 0,
        });
        message.success("Product added");
      }

      tab.total += unitPrice + itemTax;
    },

    /* ---------- DELETE ---------- */
    deleteProduct: (state, action) => {
      const tab = getActiveTab(state);
      if (!tab) return;
      ensureTabState(tab);

      const item = tab.cartItems.find((i) => i._id === action.payload._id);
      if (item) tab.total -= item.totalPrice;
      tab.cartItems = tab.cartItems.filter((i) => i._id !== action.payload._id);
      message.info("Product removed from cart");
    },

    /* ---------- INCREASE ---------- */
    increase: (state, action) => {
      const tab = getActiveTab(state);
      if (!tab) return;
      ensureTabState(tab);

      const item = tab.cartItems.find((i) => i._id === action.payload._id);
      if (!item) return;

      const itemTax = calculateTax(item.price, getItemTaxRate(item));

      item.quantity += 1;
      item.tax += itemTax;
      item.totalPrice += item.price + itemTax;
      tab.total += item.price + itemTax;
    },

    /* ---------- DECREASE ---------- */
    decrease: (state, action) => {
      const tab = getActiveTab(state);
      if (!tab) return;
      ensureTabState(tab);

      const item = tab.cartItems.find((i) => i._id === action.payload._id);
      if (!item) return;

      const itemTax = calculateTax(item.price, getItemTaxRate(item));

      item.quantity -= 1;
      item.tax -= itemTax;
      item.totalPrice -= item.price + itemTax;
      tab.total -= item.price + itemTax;

      if (item.quantity === 0) {
        tab.cartItems = tab.cartItems.filter((i) => i._id !== item._id);
      }
    },

    /* ---------- SET QUANTITY ---------- */
    setQuantity: (state, action) => {
      const tab = getActiveTab(state);
      if (!tab) return;
      ensureTabState(tab);

      const { item, quantity } = action.payload;
      const target = tab.cartItems.find((i) => i._id === item._id);
      if (!target) return;

      const qty = Math.max(0, Math.floor(quantity));
      if (qty === 0) {
        tab.total -= target.totalPrice;
        tab.cartItems = tab.cartItems.filter((i) => i._id !== target._id);
        return;
      }

      const perUnitTax = calculateTax(target.price, getItemTaxRate(target));

      const diff = qty - target.quantity;
      const perUnitTotal = target.price + perUnitTax;

      target.quantity = qty;
      target.tax = perUnitTax * qty;
      target.totalPrice = perUnitTotal * qty;
      tab.total += perUnitTotal * diff;
    },

    /* ---------- UPDATE PRICE ---------- */
    updateItemPrice: (state, action) => {
      const tab = getActiveTab(state);
      if (!tab) return;
      ensureTabState(tab);

      const { productId, price } = action.payload;
      const item = tab.cartItems.find((i) => i._id === productId);
      if (!item) return;

      const newPrice = Math.max(0, Number(price) || 0);
      const oldTotal = item.totalPrice;

      const perUnitTax = calculateTax(newPrice, getItemTaxRate(item));

      item.price = newPrice;
      item.tax = perUnitTax * item.quantity;
      item.totalPrice = (newPrice + perUnitTax) * item.quantity;

      tab.total += item.totalPrice - oldTotal;
    },

    /* ---------- UPDATE TITLE ---------- */
    updateItemTitle: (state, action) => {
      const tab = getActiveTab(state);
      if (!tab) return;
      ensureTabState(tab);
      const item = tab.cartItems.find((i) => i._id === action.payload.productId);
      if (item) item.title = String(action.payload.title || "").trim();
    },

    /* ---------- UPDATE DISCOUNT ---------- */
    updateItemDiscount: (state, action) => {
      const tab = getActiveTab(state);
      if (!tab) return;
      ensureTabState(tab);
      const { productId, discount } = action.payload;
      const item = tab.cartItems.find((i) => i._id === productId);
      if (!item) return;

      const newDiscount = Math.max(0, Math.min(100, Number(discount) || 0));
      item.discount = newDiscount;
    },

    /* ---------- PAYMENT MODE (PER TAB) ---------- */
    setPaymentMode: (state, action) => {
      const tab = ensureActiveTabExists(state);
      if (!tab) return;
      // Supports payload as:
      // - string: "Cash" | "Credit" | "Debit Card" (marks touched)
      // - object: { paymentMode: string, touched: boolean } (optional touched flag)
      const payload = action.payload;
      const isObjectPayload =
        payload != null && typeof payload === "object" && !Array.isArray(payload);

      const rawMode = isObjectPayload ? payload.paymentMode : payload;
      const touched = isObjectPayload ? payload.touched !== false : true;

      const next = String(rawMode || "").trim();
      tab.paymentMode = next || PAYMENT_MODE_CASH;
      if (touched) tab.paymentModeTouched = true;
    },

    /* ---------- RESET ---------- */
    reset: (state) => {
      const tab = getActiveTab(state);
      if (!tab) return;
      tab.cartItems = [];
      tab.total = 0;
      message.info("Cart cleared");
    },
  },
});

// Base actions (used internally)
const {
  addProduct: addProductAction,
  deleteProduct,
  increase: increaseAction,
  decrease: decreaseAction,
  setQuantity: setQuantityAction,
  updateItemPrice: updateItemPriceAction,
  updateItemTitle,
  updateItemDiscount,
  setPaymentMode,
  reset,
  addCartTab,
  removeCartTab,
  setActiveCartTab,
} = cartSlice.actions;

// Thunk action creators (payload only; tax uses backend salesTax)
export const addProduct = (payload) => (dispatch) => {
  dispatch(addProductAction(payload));
};

export const increase = (payload) => (dispatch) => {
  dispatch(increaseAction(payload));
};

export const decrease = (payload) => (dispatch) => {
  dispatch(decreaseAction(payload));
};

export const setQuantity = (payload) => (dispatch) => {
  dispatch(setQuantityAction(payload));
};

export const updateItemPrice = (payload) => (dispatch) => {
  dispatch(updateItemPriceAction(payload));
};

export { addCartTab, removeCartTab, setActiveCartTab };

export {
  deleteProduct,
  updateItemTitle,
  updateItemDiscount,
  setPaymentMode,
  reset,
};

/* -------------------- SELECTORS -------------------- */

/** Active cart for display and checkout: { cartItems, total } */
export const selectActiveCart = (state) => {
  const cart = state.cart;
  if (!cart?.activeTabId || !cart.tabs) {
    return {
      cartItems: [],
      total: 0,
      paymentMode: PAYMENT_MODE_CASH,
      paymentModeTouched: false,
    };
  }
  const tab = cart.tabs[cart.activeTabId];
  if (!tab) {
    return {
      cartItems: [],
      total: 0,
      paymentMode: PAYMENT_MODE_CASH,
      paymentModeTouched: false,
    };
  }
  return {
    cartItems: Array.isArray(tab.cartItems) ? tab.cartItems : [],
    total: Number(tab.total) || 0,
    paymentMode:
      typeof tab.paymentMode === "string" && tab.paymentMode.trim()
        ? tab.paymentMode
        : PAYMENT_MODE_CASH,
    paymentModeTouched: typeof tab.paymentModeTouched === "boolean" ? tab.paymentModeTouched : false,
  };
};

export const selectActiveTabId = (state) => state.cart?.activeTabId ?? null;

export const selectCartTabs = (state) => {
  const tabs = state.cart?.tabs;
  if (!tabs || typeof tabs !== "object") return [];
  return Object.entries(tabs).map(([id, data]) => ({
    id,
    cartItems: Array.isArray(data.cartItems) ? data.cartItems : [],
    total: Number(data.total) || 0,
    paymentMode:
      typeof data.paymentMode === "string" && data.paymentMode.trim()
        ? data.paymentMode
        : PAYMENT_MODE_CASH,
    paymentModeTouched: typeof data.paymentModeTouched === "boolean" ? data.paymentModeTouched : false,
  }));
};

export default cartSlice.reducer;
